import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ListTableComponent, ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { UserService } from '../../../../services/user.service';
import { ListService } from '../../../../shared/services/list.service';
import { ClaimsService } from '../../../../services/claims.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { InvoiceFilterComponent } from '../staff/invoices-staff-list/invoice-filter/invoice-filter.component';
import { AddInvoiceComponent } from '../provider/provider-invoice/add-invoice/add-invoice.component';

// Regular vs Adjusted toggle drives which claims list/detail page names are queried.
type PayrollType = 'regular' | 'adjust';

@Component({
  selector: 'ppl-invoice-payroll-processing',
  standalone: true,
  imports: [
    ListTableComponent,
    ButtonComponent,
    NgZorroModule,
    CommonModule,
    ReactiveFormsModule,
    InvoiceFilterComponent,
    AddInvoiceComponent,
  ],
  templateUrl: './invoice-payroll-processing.component.html',
  styleUrl: './invoice-payroll-processing.component.css',
})
export class InvoicePayrollProcessingComponent implements OnInit {
  pageId: string = PAGE_CONSTANTS.STAFF_INVOICE_PAYROLL_PROCESSING;
  private dialogSvc = inject(DialogService);

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild('addInvoiceDialog') addInvoiceDialog!: TemplateRef<any>;
  @ViewChild('submitProcessingDialog') submitProcessingDialog!: TemplateRef<any>;

  isPageRestrictionLoaded = false;
  isReadOnly = false;
  invoices: any[] = [];
  hasSearched = false;
  chequeNumbers: string[] = [];
  programName = '';
  appliedFilters: any[] = [];
  selectedRow: any;
  selectedLineItem: any;
  invoiceId: string = '';
  mode: 'add' | 'edit' | 'view' | 'actions' = 'view';
  addDialogId: string = '';

  // Selected expanded line items (drives the Submit for Processing button).
  selectedLineItems: any[] = [];
  // Provider type currently applied via the filter (IR/IC/...); used in the submit payload.
  selectedProviderType = 'IR';
  submitDialogId = '';
  processingForm: FormGroup;

  // Regular vs Adjusted selector value; sourced from the filter's Payroll Type dropdown.
  payrollType: PayrollType = 'regular';

  // Top-level list page names selected by the Regular/Adjusted toggle.
  private readonly listPageNames: Record<PayrollType, string> = {
    regular: 'invoice-payroll-regular',
    adjust: 'invoice-payroll-adjust',
  };

  // Expand (detail) page names selected by the Regular/Adjusted toggle.
  private readonly detailPageNames: Record<PayrollType, string> = {
    regular: 'invoice-payroll-regular-detail',
    adjust: 'invoice-payroll-adjust-detail',
  };

  // Filter config passed to the shared invoice filter. Adds the Payroll Type dropdown.
  filterFields = [
    { field: 'payrollType', show: true, default: 'regular' },
    { field: 'providerType', show: true, default: 'IR' },
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'status', show: true },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'processStatusLkp', show: true },
  ];

  // Top-level (pay period / provider summary) columns.
  cols = [
    {
      field: 'payPeriod',
      header: 'Pay Period',
      jsonpath: 'payPeriod',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'providerPplId',
      header: 'Provider PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerType',
      header: 'Provider Type',
      jsonpath: 'providerType',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  // Expanded (per-invoice) columns loaded from the detail API.
  lineItemCols = [
    {
      field: 'invoiceDate',
      header: 'Invoice Date',
      jsonpath: 'invoiceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'invoiceNumber',
      header: 'Invoice Number',
      jsonpath: 'invoiceNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerName',
      header: 'Participant Name',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerPplId',
      header: 'Participant PPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalLines',
      header: 'Total Lines',
      jsonpath: 'totalLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalCost',
      header: 'Total Cost',
      jsonpath: 'totalCost',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  constructor(
    private userService: UserService,
    private listService: ListService,
    private claimsService: ClaimsService,
    private allowListStore: AllowListAuthStore,
    private fb: FormBuilder
  ) {
    this.processingForm = this.fb.group({
      fromDate: [null, Validators.required],
      toDate: [null, Validators.required],
      checkDate: [null, Validators.required],
    });
  }

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.userService.setLeadEntry('invoice');
    this.setPageAccess();
  }

  getFilters(event: any) {
    const defaultDateFilter = new Date();
    defaultDateFilter.setDate(defaultDateFilter.getDate() - 30);
    const filters: any[] = [];

    filters.push({
      jsonPath: 'serviceDate',
      value: event.Timeframe ? event.Timeframe : defaultDateFilter.toLocaleDateString('en-US'),
      operator: 'greaterThan',
    });

    if (event.consumer && event.consumer.trim()) {
      filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'Equal' });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({ jsonPath: 'providerName', value: event.provider.trim(), operator: 'Equal' });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({ jsonPath: 'providerPplId', value: event.providerPplId.trim(), operator: 'Equal' });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({ jsonPath: 'consumerPplId', value: event.consumerPplId.trim(), operator: 'Equal' });
    }

    if (event.status) {
      filters.push({ jsonPath: 'businessStatus', value: event.status, operator: 'Equal' });
    }

    if (event.processStatusLkp) {
      filters.push({ jsonPath: 'processStatusLkp', value: event.processStatusLkp, operator: 'Equal' });
    }

    // Provider type is selectable in the filter (defaults to Independent / IR).
    if (event.providerType) {
      filters.push({ jsonPath: 'providerTypeLkp', value: event.providerType, operator: 'equal' });
    }

    return filters;
  }

  onSearch(event: any) {
    // Regular vs Adjusted dropdown selects which claims pages are queried.
    this.payrollType = event.payrollType === 'adjust' ? 'adjust' : 'regular';
    this.selectedProviderType = event.providerType || 'IR';

    // A fresh search invalidates any previous line-item selection.
    this.selectedLineItems = [];

    const filters = this.getFilters(event);
    this.appliedFilters = filters;
    this.searchInvoices(filters, undefined, undefined, event.chequeLookUp, this.childComponent?.recordsPerPage ?? 20);
  }

  onPageLoad(event: any) {
    this.searchInvoices(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  searchInvoices(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    this.hasSearched = true;
    const processedPayload = this.buildRequestPayload(
      this.listPageNames[this.payrollType],
      'paidPayrollScheduleId',
      filters,
      startingPageNumber,
      recordsPerPage
    );
    this.listService.getListData(processedPayload, 'claims').subscribe((data: any) => {
      const records = data.responsedata;
      records?.forEach((record: any) => {
        record.payPeriod = record.payrollStartDate + ' - ' + record.payrollEndDate;
      });

      if (isChequeLookUp) {
        if (records && records?.length) {
          this.chequeNumbers = Array.from(
            new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
        }
      } else {
        if (startingPageNumber > 1) {
          this.invoices = [...this.invoices, ...records];
        } else {
          this.invoices = records;
        }
        if (this.invoices?.length) {
          this.chequeNumbers = Array.from(
            new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
        }
      }
    });
  }

  rowClicked(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    // Carry the applied search filters into the detail call, but drop the fixed
    // providerTypeLkp scope (the detail is already keyed by schedule + provider),
    // then key the detail lookup by pay schedule + provider.
    const subRowFilter = (this.appliedFilters ?? []).filter(
      (filter: any) => filter.jsonPath !== 'providerTypeLkp'
    );

    subRowFilter.push({
      jsonPath: 'paidPayrollScheduleId',
      value: event.paidPayrollScheduleId,
      operator: 'Equal',
    });

    subRowFilter.push({
      jsonPath: 'providerId',
      value: event.providerId,
      operator: 'Equal',
    });

    this.selectedRow = event;
    this.getInvoiceLineItems(subRowFilter, event);
  }

  getInvoiceLineItems(filters: any, event: any) {
    const processedPayload = this.buildRequestPayload(
      this.detailPageNames[this.payrollType],
      'paidPayrollScheduleId',
      filters
    );
    this.listService.getListData(processedPayload, 'claims').subscribe((data: any) => {
      event.lineItems = data.responsedata;
      event.selectedLineItems = [];
      event.showLineItem = true;
      event.loaded = true;
    });
  }

  onLineItemSelection(selected: any[]) {
    this.selectedLineItems = selected ?? [];
  }

  // ─── Submit for Processing ────────────────────────────────────────────────
  openSubmitProcessing() {
    this.processingForm.reset();
    const dialogConfig: DialogConfig = {
      title: 'Submit for Processing',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.submitDialogId = this.dialogSvc.openDialog(
      this.submitProcessingDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  submitProcessing() {
    if (this.processingForm.invalid) {
      this.processingForm.markAllAsTouched();
      return;
    }

    const value = this.processingForm.value;
    const payload = {
      programCode: this.programName,
      periodStartDate: this.toIsoDate(value.fromDate),
      periodEndDate: this.toIsoDate(value.toDate),
      checkDate: this.toIsoDate(value.checkDate),
      dataType: 'IV',
      providerType: this.selectedProviderType || 'IR',
      paymentType: this.payrollType === 'adjust' ? 'Adjustment' : 'Regular',
      requestType: 'OffCycle',
      dataId: this.selectedLineItems.map((item: any) => item?.invoiceHeaderId).filter(Boolean),
    };

    this.claimsService.processInvoicePayroll(payload).subscribe(() => {
      this.closeSubmitProcessing();
      this.selectedLineItems = [];
      // Reload the grid so the just-processed records reflect their new state.
      if (this.appliedFilters?.length) {
        this.searchInvoices(this.appliedFilters, undefined, undefined, false, this.childComponent?.recordsPerPage ?? 20);
      }
    });
  }

  closeSubmitProcessing() {
    if (this.submitDialogId) {
      this.dialogSvc.closeDialog(this.submitDialogId);
      this.submitDialogId = '';
    }
  }

  private toIsoDate(d: any): string {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onAction(event: any) {
    if (event.action === 'view') {
      this.mode = 'view';
      this.selectedLineItem = event.data;
      this.invoiceId = event.data.invoiceHeaderId;
      this.openInvoice('actions');
    }
  }

  openInvoice(mode: 'add' | 'edit' | 'view' | 'actions') {
    this.mode = mode;
    const dialogConfig: DialogConfig = {
      title: 'View Invoice',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.addDialogId = this.dialogSvc.openDialog(
      this.addInvoiceDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  handleCancel(event: any) {
    if ((Array.isArray(event) && event[0]?.invoiceHeaderId) || event?.isCloseTriggeredByDialog) {
      this.dialogSvc.closeDialog(this.addDialogId);
      this.invoiceId = '';
      this.selectedLineItem = null;
    }
  }

  private buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    this.isReadOnly = !pageConfig;
    this.isPageRestrictionLoaded = true;
  }
}
